import PropTypes from 'prop-types';
import React, { FC, Fragment } from 'react';

import { StandingsType, StandingsTypeList } from '../../../typings/motorsports-standings';
import { RaceSessionType, raceSessionTypeList } from '../../../typings/race-session';
import { isValidArray } from '../../../utils/array';
import { getSessionsTabsLabels, getSessionLabels } from '../../../utils/motorsports';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import Spacer from '../../atoms/spacer/spacer';
import { ITabItem } from '../../atoms/tab-item/tab-item';
import TabsMenu from '../../molecules/tabs-menu/tabs-menu';

import './motorsports-tabs-switch.scss';

type MotorsportsStandingType = RaceSessionType | StandingsType;

const getMotorsportsStandingTypeList = () => [...raceSessionTypeList, ...StandingsTypeList];

/**
 * This component is an "extension" of existing src/components/organisms/tabs-switch/tabs-switch.tsx
 * In case of fixes/features we should keep the 2 components in-sync
 */
export interface IMotorsportsTabsSwitch {
  // sessions: Record<RaceSessionType, IMotorsportsTabsSession>;
  sessions: Array<IMotorsportsTabsSession>;
  isLgp?: boolean;
  hasLgpAnalytics?: boolean;
}
export interface IMotorsportsTabsSession {
  customDataAttributes: object;
  isActive?: boolean;
  sessionType: MotorsportsStandingType;
  ssi: string;
  startDateTime?: string;
}

export interface IMotorsportsTab {
  id: MotorsportsStandingType;
  index: number;
  ssi: string;
}

const labels = getSessionLabels();

const renderLgpChips = (sessions: ITabItem[]) => {
  return (
    <Fragment>
      {sessions.length === 0 && null}
      {sessions.length === 1 && <h2>Prove {sessions[0].id && labels[sessions[0].id]}</h2>}
      {sessions.length > 1 && <TabsMenu tabsList={sessions} isChip variant="chips" />}
    </Fragment>
  );
};

const classNames = (): IClassNames =>
  makeClassNames({
    MotorsportsTabsSwitchShadow: 'motorsports-tabs-switch__shadow',
    MotorsportsTabsSwitchContent: 'motorsports-tabs-switch__content',
  });

const MotorsportsTabsSwitch: FC<IMotorsportsTabsSwitch> = ({ sessions, isLgp = false }: IMotorsportsTabsSwitch) => {
  // Get TABS LIST for <TabsMenu/>
  // const { sessions, isLgp } = props;
  if (!isValidArray(sessions)) {
    // WCG error?
    console.warn('MotorsportsTabsSwitch - Unexpected data', sessions); // eslint-disable-line no-console
    return <Fragment>Unexpected error in data received, check log</Fragment>;
  }

  const mapSessionsLabels = getSessionsTabsLabels();
  const tabsList: Array<ITabItem> = sessions.map((session: IMotorsportsTabsSession) => {
    const { customDataAttributes, isActive, sessionType } = session;

    return {
      id: `${sessionType}`,
      label: mapSessionsLabels[sessionType],
      itemState: isActive ? 'active' : '',
      attributes: customDataAttributes, // data-attributes used by URL UPDATE, TRACKING and ADV REFRESH
    };
  });

  // Get TABS CONTENT -> FIXME: define INTERFACE for EACH item
  const tabsContent: Array<IMotorsportsTab> = sessions.map((session: IMotorsportsTabsSession, index: number) => {
    const { sessionType, ssi } = session;

    return {
      index,
      id: sessionType,
      ssi,
    };
  });

  const cn = classNames();

  return (
    <Fragment>
      <div className={cn.MotorsportsTabsSwitchShadow} />
      {isLgp ? renderLgpChips(tabsList) : tabsList && tabsList.length > 0 && <TabsMenu tabsList={tabsList} />}

      {tabsContent &&
        tabsContent.map((item: IMotorsportsTab, index: number) => {
          return (
            <Fragment key={item.id}>
              <div
                className={cn.MotorsportsTabsSwitchContent}
                id={`${cn.MotorsportsTabsSwitchContent}--${item.id ?? index}`} // INDEX could NEVER be used because MAPPING in tabsContent() returns "id: sessionType" which is MANDATORY
              >
                <Spacer size={20} />
                {`${'<!--#include virtual="'}${item.ssi}${'" -->'}`}
              </div>
            </Fragment>
          );
        })}
    </Fragment>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  MotorsportsTabsSwitch.propTypes = {
    sessions: PropTypes.arrayOf(
      PropTypes.shape({
        customDataAttributes: PropTypes.object.isRequired,
        sessionType: PropTypes.oneOf(getMotorsportsStandingTypeList()).isRequired,
        ssi: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  };
}

export default MotorsportsTabsSwitch;
